import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.js';
import { Icon } from '../../lib/icons.js';
import { haptic } from '../../lib/native.js';
import { pushOverlay } from '../../lib/overlays.js';

interface ConfirmReq {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'alert';
  icon?: string;
  resolve: (ok: boolean) => void;
}

let current: ConfirmReq | null = null;
const ls = new Set<() => void>();
const set = (r: ConfirmReq | null): void => {
  current = r;
  ls.forEach((f) => f());
};

/** Promise-based confirmation dialog (the in-page replacement for window.confirm). */
export function confirmDialog(opts: Omit<ConfirmReq, 'resolve'>): Promise<boolean> {
  current?.resolve(false);
  haptic('warning');
  return new Promise((resolve) => set({ ...opts, resolve }));
}

export function DialogHost(): ReactNode {
  const req = useSyncExternalStore(
    (f) => {
      ls.add(f);
      return () => ls.delete(f);
    },
    () => current,
    () => current,
  );
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!req) return;
    okRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        req.resolve(false);
        set(null);
      }
    };
    window.addEventListener('keydown', onKey);
    const pop = pushOverlay(() => {
      req.resolve(false);
      set(null);
    });
    return () => {
      window.removeEventListener('keydown', onKey);
      pop();
    };
  }, [req]);
  if (!req) return null;
  const done = (ok: boolean): void => {
    req.resolve(ok);
    set(null);
  };
  return createPortal(
    <div className="dialog-root">
      <div className="sheet-scrim" onClick={() => done(false)} />
      <div className="dialog glass glass-raised" role="alertdialog" aria-modal="true" aria-label={req.title}>
        {req.icon && (
          <span className="dialog-icon" data-tone={req.tone ?? 'default'}>
            <Icon name={req.icon} size={22} />
          </span>
        )}
        <h2>{req.title}</h2>
        {req.body && <div className="dialog-body">{req.body}</div>}
        <div className="dialog-actions">
          <Button onClick={() => done(false)}>{req.cancelLabel ?? 'Cancel'}</Button>
          <Button ref={okRef} variant={req.tone === 'alert' ? 'alert' : 'primary'} onClick={() => done(true)} data-testid="confirm-ok">
            {req.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
